import { useEffect, useRef, useState } from "react";
import { Button } from "components/Button";
import { useHistory } from "react-router";
import { iconArrowRight } from "assets/icons";
import { asParams } from "utils/asParams";
import { BaseRoutes } from "routes/BaseRoutes";
import { convertDateToSendPOST, convertDateToShowInput, formattingCNPJ } from "utils/formattingInputs";
import { FilterOptionProps, FilterProps } from "components/TableModified/TableModifiedFilters";
import { useToast } from "hooks/useToast";
import { useAuth } from "hooks/useAuth";

interface Property {
  name?: string,
  value?: string,
}

interface Lead {
  leadId: number,
  insertDate: string,
  clearsaleEntityId?: number,
  firstname: string,
  email: string,
  cnpj: string,
  company: string,
  website: string,
  logo: string,
  phone: string,
  ramal: string,
  socialMediaLink: string,
  segmentId: number,
  ecommercePlatformId: number,
  billingRangeId: number,
  amountRangeId: number,
  emailSupport: string,
  phoneSupport: string,
  origin: string,
  leadStatus: string,
  sellerClassification: string,
  userName: string,
}

interface LeadRowProps {
  insertDate: string,
  leadStatus: string,
  company: string,
  sellerClassification: string,
  username: string,
  origin: string,
  cnpj: string,
  actions: JSX.Element[],
}

const initialTableFilters:FilterProps = {
  'company': {
    type: "text",
    label: 'Nome da loja',
    value: '',
  },
  'insertDate': {
    type: "datepicker",
    label: 'Data do cadastro',
    value: '',
    lineBreak: true
  },
  'sellerClassification': {
    label: 'Categoria',
    value: '',
    options: [
      {label: '', value: ''},
    ],
    lineBreak: true
  },
  'origin': {
    label: 'Origem',
    value: '',
    options: [
      {label: 'Backoffice', value: 'BACKOFFICE'},
      {label: 'Confi', value: 'SITE_CONFI'},
      {label: 'Clearsale', value: 'CLEAR_EXTERNAL'},
    ],
    lineBreak: true
  },
  'userId': {
    label: 'Responsável',
    value: '',
    options: [
      {label: '', value: ''},
    ],
    lineBreak: true
  }
};

export const useLeadsTableDisapproved = () => {
  const searchTextFormRef = useRef<any>(null);
  const {push} = useHistory();
  const {setToast} = useToast();
  const {connectApi, connectAllApi} = useAuth();

  const [properties, setProperties] = useState<Property[]>();
  const [leadRows, setLeadRows] = useState<LeadRowProps[]>([]);
  const [tableFilters, setTableFilters] = useState<FilterProps>(initialTableFilters);
  const [firstCall, setFirstCall] = useState(true);
  
  const [searchText, setSearchText] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [leadsPerPage, setLeadsPerPage] = useState<number>(10);
  const [leadsCount, setLeadsCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    getParams();
  }, [])

  useEffect(() => {
    !firstCall && getLeads();
  }, [searchText, tableFilters, pageNumber, leadsPerPage])

  const filtersAsParams = ():string => {
    var filters = [];
    for (let [key, value] of Object.entries(tableFilters)) {
      const valueChecked = key === "insertDate" && value.value ? convertDateToSendPOST(value.value || "") : value.value
      filters.push({key: key, value: valueChecked ?? ''});
    }
    filters.push({key: 'PageNumber', value: pageNumber.toString()});
    filters.push({key: 'RowsPerPage', value: leadsPerPage.toString()});
    return asParams(filters);
  };

  const rowActionButton = (route: string, param?: string) => {
    return (
      <Button 
        label="" 
        size="small" 
        icon={iconArrowRight} 
        onlyIcon={true} 
        onClick={() => push(route, param)}
      />
    )
  };

  /* EVENT HANDLERS */
  const handleFilterChange = (event: any, name?: string): void => {
    const eventName = name || event.target.name;
    const eventValue = name ? new Date(event) : event.target.value;
    setTableFilters({
      ...tableFilters,
      [eventName]: {
        ...tableFilters[eventName],
        value: eventValue,
      }
    })
  }

  const handleFilterRemove = (filterName: string): void => {
    setTableFilters({
      ...tableFilters,
      [filterName]: {
        ...tableFilters[filterName],
        value: '',
      }
    })
  }

  const handleSearchTextFormSubmit = (event: any) => {
    event.preventDefault();
    const text = event.target.searchText.value;
    setSearchText(text);
  }

  const handleSearchTextRemove = ():void => {
    searchTextFormRef.current.reset();
    setSearchText('');
  }

  const handlePageSelected = (page: number):void => {
    setPageNumber(page);
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>):void => {
    const eventName = event.target.name;
    const eventValue = event.target.value;
    setLeadsPerPage(parseInt(eventValue));
  }

  const originText = (props: string) => (
    {
    "BACKOFFICE": "Backoffice",
    "SITE_CONFI": "Confi",
    "CLEAR_EXTERNAL": "Clearsale"
  })[props]

  /* API CALLS */
  const getLeads = async () => {
    setLoading(true);
    connectApi({
      type: 'get',
      url: `leads?leadStatusId=2&${filtersAsParams()}`,
    }).then((response) => {
      setError(false);
      let responseData = response.data;
      let leads: Lead[] = responseData.data;
      let leadsCount:number = responseData.count || 0;
      setLeadsCount(leadsCount);

      if(leads){
        setLeadRows([
          ...leads.map((lead: Lead) => ({
            insertDate: convertDateToShowInput(lead.insertDate),
            leadStatus: lead.leadStatus || "-",
            company: lead.company || "-",
            sellerClassification: lead.sellerClassification || "-",
            origin: String(originText(lead.origin) || "-")  ,
            cnpj: lead.cnpj ? formattingCNPJ(lead.cnpj) : "-",
            username: lead.userName || '-',
            actions: [rowActionButton(BaseRoutes.sellersLeadEdit.replaceAll(':id', String(lead.leadId)), lead.company || "Companhia")]
          }))
        ])
      }else{
        setLeadRows([])
      }
    }).catch((error) => {
      setError(true);
    }).finally(() => setLoading(false));
  }

  const getParams = async () => {
    connectAllApi([
      {type: 'get', url: 'sellers/properties'},
      {type: 'get', url: "/users?pageNumber=1&rowsPerPage=10000"},
      {type: 'get', url: "/leads/summary"},
    ]).then((responses) => {
      setFirstCall(false);
      let sellerClassificationOptions = [] as FilterOptionProps[];
      let usersOptions = [] as FilterOptionProps[];

      if(responses[0].status !== "rejected") {
        const data = responses[0].value.data;
        const amountProperties = data.amountRange.map((item:any) => item.sellerClassification);

        sellerClassificationOptions = Array.from(new Set(amountProperties)).map((item:any) => ({
          label: item,
          value: item
        }));

      }else{
        setToast({
          isActive: true,
          message: "Falha ao carregar combo de Categorias",
          type: "error",
          timeToRemoveToast: 3000
        })
      }

      if(responses[1].status !== "rejected") {
        const data = responses[1].value.data;
        usersOptions = data.map((item:any) => ({
          label: item.userName,
          value: item.userId
        }))
      }else{
        setToast({
          isActive: true,
          message: "Falha ao carregar combo de Responsáveis",
          type: "error",
          timeToRemoveToast: 3000
        })
      }

      if(responses[2].status !== "rejected") {
        const data = responses[2].value.data;
        delete data[0]
        delete data[1]
        
        setProperties(data);
      }else{
        setToast({
          isActive: true,
          message: "Falha ao carregar combo de Categorias",
          type: "error",
          timeToRemoveToast: 3000
        })
      }

      setTableFilters({
        ...tableFilters,
        sellerClassification: {
          ...tableFilters.sellerClassification,
          options: sellerClassificationOptions
        },
        userId: {
          ...tableFilters.userId,
          options: usersOptions
        }
      })
    });
  }

  return {
    loading,
    error,
    properties,
    leadRows,
    leadsCount,
    searchText,
    searchTextFormRef,
    pageNumber,
    leadsPerPage,
    handleSearchTextFormSubmit,
    handleSearchTextRemove,
    handlePageSelected,
    handleRowsPerPageChange,    
    tableFilters,
    handleFilterChange,
    handleFilterRemove,
  };
}